import {
  useGetList,
  useLocaleState,
  useTranslate,
  type Identifier,
} from "ra-core";

import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Contract } from "../types";

/** Contracts of a company, newest year first. Read-only: the technical team
 *  edits contracts (enforced by RLS, see migration 20261001130000). */
export const ContractsTab = ({ companyId }: { companyId: Identifier }) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { data, isPending } = useGetList<Contract>("contracts", {
    filter: { company_id: companyId },
    sort: { field: "year", order: "DESC" },
    pagination: { page: 1, perPage: 200 },
  });

  if (isPending) return null;
  if (!data?.length) {
    return (
      <p className="pt-2 text-sm text-muted-foreground">
        {translate("crm.construnario.contracts.empty")}
      </p>
    );
  }

  const money = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
  const date = (value: string | null) =>
    value
      ? new Date(value).toLocaleDateString(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        })
      : "";

  return (
    <div className="flex flex-col gap-2 pt-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {translate("crm.construnario.contracts.year")}
            </TableHead>
            <TableHead>
              {translate("crm.construnario.contracts.items")}
            </TableHead>
            <TableHead className="text-right">
              {translate("crm.construnario.contracts.amount")}
            </TableHead>
            <TableHead>
              {translate("crm.construnario.contracts.period")}
            </TableHead>
            <TableHead>
              {translate("crm.construnario.contracts.invoice")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((contract) => (
            <TableRow key={contract.id}>
              <TableCell className="font-medium">
                {contract.year}
                {contract.previous_contract_id ? (
                  <Badge variant="outline" className="ml-2">
                    {translate("crm.construnario.contracts.renewal")}
                  </Badge>
                ) : null}
              </TableCell>
              <TableCell className="max-w-72 whitespace-normal">
                {contract.items.join(", ")}
                {contract.remarks ? (
                  <div className="text-xs text-muted-foreground">
                    {contract.remarks}
                  </div>
                ) : null}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {contract.amount != null ? money.format(contract.amount) : ""}
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs">
                {date(contract.period_start)}
                {contract.period_end ? ` – ${date(contract.period_end)}` : ""}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {contract.invoice_number ? (
                  <span className="mr-2 text-xs">
                    {contract.invoice_number}
                  </span>
                ) : null}
                {contract.paid ? (
                  <Badge variant="secondary">
                    {translate("crm.construnario.contracts.paid")}
                    {contract.paid_on ? ` ${date(contract.paid_on)}` : ""}
                  </Badge>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="text-xs text-muted-foreground">
        {translate("crm.construnario.contracts.read_only")}
      </p>
    </div>
  );
};
